/**
 * Animated background effects - SHARED module.
 *
 * A pure CSS layer (`@keyframes` + gradients + blur) sitting absolutely on top
 * of the base background and behind the content. SSR-safe (no JS), so it works
 * in the admin canvas, on the public site AND inside standalone SPAs (the
 * web_account login and account background) - importable through
 * `@wa/widgets/bg-effect` WITHOUT pulling in the whole render engine.
 *
 * Colours, speed and blur arrive as inline CSS variables; the rules themselves
 * come from `bgEffectCss.ts`, which is split per effect so an instance injects
 * only the chunk it actually uses. The effect list lives in `../bgEffects.js`,
 * the single source of truth shared with the widget registry.
 *
 * Only active on a `solid` / `gradient` base, so it can never cover a
 * background photo or video.
 */
import type { CSSProperties, ReactNode } from "react";
import { type BgEffectId, isBgEffectId } from "../bgEffects.js";
import { BG_EFFECT_PARTS, bgEffectCss } from "./bgEffectCss.js";
import { ROUND_PX } from "./rounded.js";

export { BG_EFFECT_CSS, BG_EFFECT_BASE_CSS, BG_EFFECT_PAUSE_CSS, BG_EFFECT_PART_CSS, BG_EFFECT_PARTS, bgEffectCss } from "./bgEffectCss.js";

const num = (v: unknown, fallback = 0): number => (typeof v === "number" ? v : fallback);
const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);
const bool = (v: unknown, fallback = false): boolean => (typeof v === "boolean" ? v : fallback);

/** Does this block have an active animated background effect?
 *  (only on a solid / gradient base) */
export function hasBgEffect(props: Record<string, unknown>): boolean {
  const t = str(props.bgType, "solid");
  return (t === "solid" || t === "gradient") && isBgEffectId(props.bgEffect);
}

/**
 * Corner radii of the `rounded` prop - the SAME table the sections round with.
 *
 * This used to be a hand-kept copy of BlockView's map, because this module is
 * the standalone entry `@wa/widgets/bg-effect` and must stay importable without
 * the render engine (the login background does exactly that). The table now
 * lives in `rounded.ts`, which is pure and imports nothing, so there is no copy
 * left to drift.
 */
export const BG_EFFECT_ROUND_PX: Record<string, string> = ROUND_PX;

/** The radius the pinned clip has to round off, as a CSS length.
 *  Card mode (`bgCard`) puts the radius on the CONTENT box instead of on the
 *  section, so the section itself is square and the clip must be too. */
function pinnedRadius(props: Record<string, unknown>): string {
  if (bool(props.bgCard)) return "0";
  return BG_EFFECT_ROUND_PX[str(props.rounded, "none")] ?? "0";
}

/** Animated background effect layer - assumes the container is
 *  `position:relative` + `overflow:hidden` and that the content sits higher
 *  (`zIndex>0`). Returns `null` when no effect is active. Shared between
 *  BlockView (hero/cta), the admin canvas scaffold (section/columns) and the
 *  web_account background - see [[canvas-container-scaffold-gotcha]].
 *
 *  `bgEffectFixed` pins the layer to the viewport instead of to the section, so
 *  the content scrolls over a background that stands still. That needs one
 *  extra wrapper, because the clip has to sit on an element the fixed layer
 *  does NOT use as its containing block - see `BG_EFFECT_BASE_CSS`. */
export function BgEffectLayer({ props }: { props: Record<string, unknown> }): ReactNode {
  if (!hasBgEffect(props)) return null;
  const effect = props.bgEffect as BgEffectId;
  const vars = {
    "--wa-bge-c1": str(props.bgEffectColor1) || "var(--color-primary)",
    "--wa-bge-c2": str(props.bgEffectColor2) || "var(--color-primary-2)",
    "--wa-bge-c3": str(props.bgEffectColor3) || "var(--color-accent-brand)",
    "--wa-bge-dur": `${Math.max(4, num(props.bgEffectSpeed, 16))}s`,
    "--wa-bge-blur": `${Math.max(0, num(props.bgEffectBlur, 60))}px`,
  } as CSSProperties;
  const opacity = Math.min(1, Math.max(0.1, num(props.bgEffectIntensity, 90) / 100));
  const animate = bool(props.bgEffectAnimate, true);
  const layer = (
    <div className={`wa-bge wa-bge-${effect}${animate ? "" : " wa-bge-static"}`} aria-hidden style={{ ...vars, opacity }}>
      <style dangerouslySetInnerHTML={{ __html: bgEffectCss(effect) }} />
      {BG_EFFECT_PARTS[effect].map((cls) => (
        <span key={cls} className={cls} />
      ))}
    </div>
  );
  if (!bool(props.bgEffectFixed)) return layer;
  return (
    <div className="wa-bge-fix" aria-hidden style={{ "--wa-bge-r": pinnedRadius(props) } as CSSProperties}>
      {layer}
    </div>
  );
}
